/**
 * Multi-select dropdown view with search for mobile filter sheets.
 *
 * Renders a collapsible trigger showing selected count, a search input
 * when expanded, and a scrollable checklist of options using MobileFilterOption.
 * Uses inline styles for dark mode because NativeWind `dark:` classes
 * don't resolve inside GlueStack Actionsheet/Modal portals on native iOS.
 * @module components/shared/MobileFilterSheet/MobileMultiSelect/MobileMultiSelectView
 */

import { ChevronDown, ChevronUp, Search, X } from "lucide-react-native";
import { memo } from "react";

import MobileFilterOption from "../MobileFilterOption";
import MobileSelectAllOption from "../MobileSelectAllOption";

import { Icon } from "@/components/atoms/Icon";
import { Input } from "@/components/atoms/Input";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import type { FilterOption } from "@/types/filter";

/**
 * Theme-aware colors for the multi-select trigger and dropdown.
 * Uses direct hex values because `dark:` NativeWind classes don't resolve
 * inside GlueStack Actionsheet/Modal portals on native iOS.
 */
type ThemeMode = "light" | "dark";

const MULTISELECT_COLORS: Record<
  ThemeMode,
  {
    readonly bg: string;
    readonly border: string;
    readonly text: string;
    readonly textMuted: string;
    readonly accent: string;
  }
> = {
  light: {
    bg: "#FFFFFF",
    border: "#D3D3D3",
    text: "#171717",
    textMuted: "#737373",
    accent: "#0066CC",
  },
  dark: {
    bg: "#1C1C1E",
    border: "#38383A",
    text: "#E5E5EA",
    textMuted: "#8E8E93",
    accent: "#0A84FF",
  },
};

/**
 * Returns the trigger text color based on selection state and theme.
 * @param theme - Current light/dark theme mode
 * @param hasSelection - Whether any filter options are selected
 */
const getTriggerTextColor = (
  theme: ThemeMode,
  hasSelection: boolean
): string =>
  hasSelection
    ? MULTISELECT_COLORS[theme].text
    : MULTISELECT_COLORS[theme].textMuted;

/** Props for MobileMultiSelectView */
interface MobileMultiSelectViewProps {
  /** Label shown above the trigger */
  readonly label: string;
  /** Placeholder when nothing selected */
  readonly placeholder: string;
  /** Filtered options to display in the checklist */
  readonly options: readonly FilterOption[];
  /** Currently selected option IDs */
  readonly selectedIds: readonly string[];
  /** Number of selected options (for badge) */
  readonly selectedCount: number;
  /** Whether the dropdown is expanded */
  readonly isOpen: boolean;
  /** Ref for click-away boundary */
  readonly rootRef: React.Ref<HTMLElement>;
  /** Ref for focus restoration to the trigger */
  readonly toggleRef: React.Ref<HTMLElement>;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Current search text */
  readonly searchText: string;
  /** Whether a server-side search is in progress */
  readonly isSearching: boolean;
  /** Toggle dropdown open/closed */
  readonly onToggleOpen: () => void;
  /** Search text change handler */
  readonly onSearchChange: (text: string) => void;
  /** Clear search text */
  readonly onClearSearch: () => void;
  /** Toggle a single option */
  readonly onToggle: (id: string) => void;
  /** Clear all selections */
  readonly onClear: () => void;
}

const toTestIdPart = (value: string): string =>
  value.toLocaleLowerCase("en").replace(/[^a-z0-9]+/g, "-");

/**
 * Resolves the selected-count or empty placeholder text for the trigger.
 * @param selectedCount - Number of selected options
 * @param placeholder - Empty-selection copy
 * @returns Trigger label
 */
const resolveSelectionLabel = (
  selectedCount: number,
  placeholder: string
): string => (selectedCount > 0 ? `${selectedCount} selected` : placeholder);

/**
 * Resolves the color palette for a given dark mode flag.
 * @param isDark - Whether dark mode is active
 */
const resolveColors = (isDark: boolean) =>
  MULTISELECT_COLORS[isDark ? "dark" : "light"];

/**
 * Text color style.
 * design-system: dynamic-style waiver — data-driven value (theme-resolved color)
 * @param color - Resolved text color
 */
const mmsTextColorStyle = (color: string) => ({ color });

/**
 * Border color style.
 * design-system: dynamic-style waiver — data-driven value (theme-resolved border)
 * @param color - Resolved border color
 */
const mmsBorderColorStyle = (color: string) => ({ borderColor: color });

/**
 * Trigger/dropdown chrome colors (background + border).
 * design-system: dynamic-style waiver — data-driven value (theme-resolved chrome)
 * @param isDark - Whether dark mode is active
 */
const mmsChromeStyle = (isDark: boolean) => ({
  backgroundColor: resolveColors(isDark).bg,
  borderColor: resolveColors(isDark).border,
});

/**
 * Renders a multi-select dropdown with search input and checklist.
 * @param root0 - View props
 * @param root0.label - Dropdown label
 * @param root0.placeholder - Placeholder text
 * @param root0.options - Filtered options
 * @param root0.selectedIds - Selected IDs
 * @param root0.selectedCount - Selected count
 * @param root0.isOpen - Whether expanded
 * @param root0.rootRef - Click-away boundary ref
 * @param root0.toggleRef - Trigger focus ref
 * @param root0.isDark - Whether dark mode is active
 * @param root0.searchText - Search query
 * @param root0.isSearching - Whether server search is loading
 * @param root0.onToggleOpen - Toggle handler
 * @param root0.onSearchChange - Search change handler
 * @param root0.onClearSearch - Clear search handler
 * @param root0.onToggle - Option toggle handler
 * @param root0.onClear - Clear all handler
 */
const MobileMultiSelectView = ({
  label,
  placeholder,
  options,
  selectedIds,
  selectedCount,
  isOpen,
  rootRef,
  toggleRef,
  isDark,
  searchText,
  isSearching,
  onToggleOpen,
  onSearchChange,
  onClearSearch,
  onToggle,
  onClear,
}: MobileMultiSelectViewProps) => (
  <Stack ref={rootRef as React.Ref<HTMLDivElement>} className="gap-1">
    <Stack direction="horizontal" className="items-center justify-between">
      <Text
        variant="unstyled"
        className="text-title-sm"
        // eslint-disable-next-line no-restricted-syntax -- data-driven theme text color (portal-safe hex; dark: unresolved in GlueStack portal)
        UNSAFE_style={mmsTextColorStyle(resolveColors(isDark).text)}
      >
        {label}
      </Text>
      {selectedCount > 0 ? (
        <Pressable onPress={onClear}>
          <Text
            variant="caption"
            // eslint-disable-next-line no-restricted-syntax -- data-driven theme accent color (portal-safe hex; dark: unresolved in GlueStack portal)
            UNSAFE_style={mmsTextColorStyle(resolveColors(isDark).accent)}
          >
            Clear
          </Text>
        </Pressable>
      ) : null}
    </Stack>

    {/* Trigger button */}
    <Pressable
      testID={`mobile-filter:${toTestIdPart(label)}-trigger`}
      accessibilityLabel={`${label}: ${resolveSelectionLabel(selectedCount, placeholder)}`}
      ref={toggleRef as React.Ref<React.ComponentRef<typeof Pressable>>}
      onPress={onToggleOpen}
      className="flex-row items-center justify-between rounded-sm border px-3 py-3"
      // eslint-disable-next-line no-restricted-syntax -- data-driven theme chrome colors (portal-safe hex; dark: unresolved in GlueStack portal)
      UNSAFE_style={mmsChromeStyle(isDark)}
    >
      <Text
        variant="body"
        // eslint-disable-next-line no-restricted-syntax -- data-driven selection/theme text color (portal-safe hex; dark: unresolved in GlueStack portal)
        UNSAFE_style={mmsTextColorStyle(
          getTriggerTextColor(isDark ? "dark" : "light", selectedCount > 0)
        )}
      >
        {resolveSelectionLabel(selectedCount, placeholder)}
      </Text>
      <Icon
        as={isOpen ? ChevronUp : ChevronDown}
        size="xs"
        color={getTriggerTextColor(
          isDark ? "dark" : "light",
          selectedCount > 0
        )}
      />
    </Pressable>

    {/* Expanded dropdown with search + checklist */}
    {isOpen ? (
      <Stack
        className="overflow-hidden rounded-none border"
        // eslint-disable-next-line no-restricted-syntax -- data-driven theme chrome colors (portal-safe hex; dark: unresolved in GlueStack portal)
        UNSAFE_style={mmsChromeStyle(isDark)}
      >
        <Stack className="px-2 pt-2">
          {/* Search input */}
          <Input
            size="sm"
            variant="outline"
            leadingIcon={Search}
            leadingIconSize="xs"
            leadingIconTone="muted"
            trailingIcon={searchText.length > 0 ? X : undefined}
            trailingIconSize="xs"
            trailingIconTone="muted"
            onTrailingIconPress={onClearSearch}
            // eslint-disable-next-line no-restricted-syntax -- data-driven theme border color (portal-safe hex; dark: unresolved in GlueStack portal)
            UNSAFE_style={mmsBorderColorStyle(resolveColors(isDark).border)}
            field={{
              placeholder: "Search...",
              value: searchText,
              onChangeText: onSearchChange,
              autoCorrect: false,
              style: mmsTextColorStyle(resolveColors(isDark).text),
            }}
          />
        </Stack>

        {/* Options checklist */}
        <ScrollView
          className="max-h-48"
          nestedScrollEnabled
          keyboardShouldPersistTaps="handled"
        >
          {isSearching ? (
            <Text
              variant="body"
              className="px-4 py-3"
              // eslint-disable-next-line no-restricted-syntax -- data-driven theme muted text color (portal-safe hex; dark: unresolved in GlueStack portal)
              UNSAFE_style={mmsTextColorStyle(resolveColors(isDark).textMuted)}
            >
              Searching...
            </Text>
          ) : null}
          {!isSearching && options.length === 0 ? (
            <Text
              variant="body"
              className="px-4 py-3"
              // eslint-disable-next-line no-restricted-syntax -- data-driven theme muted text color (portal-safe hex; dark: unresolved in GlueStack portal)
              UNSAFE_style={mmsTextColorStyle(resolveColors(isDark).textMuted)}
            >
              No results
            </Text>
          ) : null}
          {/* "All" option — clears selection, shown as active when nothing selected */}
          {!isSearching ? (
            <MobileSelectAllOption
              label={placeholder}
              isActive={selectedCount === 0}
              textColor={resolveColors(isDark).text}
              textMutedColor={resolveColors(isDark).textMuted}
              onPress={onClear}
            />
          ) : null}
          {options.map(opt => (
            <MobileFilterOption
              key={opt.id}
              id={opt.id}
              label={opt.label}
              isSelected={selectedIds.includes(opt.id)}
              onToggle={onToggle}
            />
          ))}
        </ScrollView>
      </Stack>
    ) : null}
  </Stack>
);

MobileMultiSelectView.displayName = "MobileMultiSelectView";

export default memo(MobileMultiSelectView);
